import { useState } from "react";
import { motion } from "framer-motion";
import { Car, ShieldCheck, Fuel, Wrench, MessageCircle } from "lucide-react";
import { waLink } from "@/lib/api";

export default function CarRates() {
  const [km, setKm] = useState(60);
  const isLocal = km <= 50;
  const rate = isLocal ? 75 : 20;
  const fare = km * rate;

  return (
    <section id="car-rates" className="py-20 lg:py-28 bg-surface" data-testid="car-rates-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl mb-12"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-terra mb-3">06 · Car Rentals & Rates</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink">
            Transparent fares. <span className="italic text-terra">Zero surprises.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 grid sm:grid-cols-2 gap-6"
          >
            <div className="bg-white border border-ink/10 rounded-2xl p-7 relative overflow-hidden" data-testid="rate-card-local">
              <Car className="text-foam mb-4" size={28} />
              <p className="font-mono text-[11px] uppercase tracking-widest text-ink/50">Local Puri · up to 50 km</p>
              <p className="mt-2 font-serif text-5xl font-black text-ink">
                ₹75<span className="text-lg font-sans font-medium text-ink/50">/km</span>
              </p>
              <p className="mt-3 text-sm text-ink/70 leading-relaxed">
                Temple hops, beach runs and market rounds across town with a driver who waits while you explore.
              </p>
            </div>
            <div className="bg-white border-2 border-terra/40 text-ink rounded-2xl p-7 relative overflow-hidden" data-testid="rate-card-outstation">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-terra/10 blur-2xl" />
              <ShieldCheck className="text-terra mb-4" size={28} />
              <p className="font-mono text-[11px] uppercase tracking-widest text-terra">Outstation · over 50 km</p>
              <p className="mt-2 font-serif text-5xl font-black text-ink">
                ₹20<span className="text-lg font-sans font-medium text-ink/50">/km</span>
              </p>
              <p className="mt-3 text-sm text-ink/70 leading-relaxed">Per-km fare covers car, fuel and driver. Tolls, parking, driver's food and night halt are charged at actuals.</p>
              <ul className="mt-4 space-y-2">
                {["Driver & fuel included", "Tolls & parking at actuals", "Night halt & driver food at actuals"].map((x) => (
                  <li key={x} className="flex items-center gap-2 text-xs text-ink/80">
                    {x.includes("Driver &") ? <Car size={13} className="text-terra" /> : x.includes("Toll") ? <Fuel size={13} className="text-terra" /> : <Wrench size={13} className="text-terra" />}
                    {x}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 bg-white border border-ink/10 rounded-2xl p-7"
            data-testid="fare-calculator"
          >
            <p className="font-mono text-[11px] uppercase tracking-widest text-ink/50 mb-1">Instant Fare Estimator</p>
            <h3 className="font-serif text-2xl font-bold text-ink">How far are you going?</h3>
            <div className="mt-6">
              <input
                type="range"
                min="10"
                max="600"
                step="5"
                value={km}
                onChange={(e) => setKm(Number(e.target.value))}
                data-testid="fare-calculator-distance-input"
                className="w-full accent-[#D95D39]"
              />
              <div className="mt-2 flex justify-between text-xs text-ink/50 font-mono">
                <span>10 km</span>
                <span data-testid="fare-calculator-distance-value" className="text-ink font-semibold">{km} km · {isLocal ? "Local rate" : "Outstation rate"}</span>
                <span>600 km</span>
              </div>
            </div>
            <div className="mt-6 bg-sand rounded-xl p-5 text-center">
              <p className="text-xs text-ink/50 font-mono uppercase tracking-widest">Estimated fare</p>
              <motion.p
                key={fare}
                initial={{ scale: 0.9, opacity: 0.5 }}
                animate={{ scale: 1, opacity: 1 }}
                className="font-serif text-5xl font-black text-terra mt-1"
                data-testid="fare-calculator-result"
              >
                ₹{fare.toLocaleString("en-IN")}
              </motion.p>
              <p className="text-xs text-ink/50 mt-1">{km} km × ₹{rate}/km{!isLocal && " · tolls, parking, night halt & driver food extra"}</p>
            </div>
            <a
              href={waLink(`Hi Random Rider! I need a car for about ${km} km. Estimated fare ₹${fare.toLocaleString("en-IN")} at ₹${rate}/km${isLocal ? "" : " (tolls, parking, night halt & driver food extra)"}. Please confirm availability.`)}
              target="_blank"
              rel="noreferrer"
              data-testid="fare-calculator-whatsapp-button"
              className="mt-5 flex items-center justify-center gap-2 w-full bg-[#25D366] text-white text-sm font-semibold py-3 rounded-full hover:brightness-110 transition-all"
            >
              <MessageCircle size={15} /> Confirm this fare on WhatsApp
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
